import React, { useEffect, useRef, useState } from "react";
import { useInstallPrompt } from "./useInstallPrompt";

// Vista de prueba de la PWA de Montacargas.
// Vive fuera del scope de Auth0 (ver src/index.js): no usa useAuth0 ni nada
// que dependa de Auth0Provider, para no depender del proveedor de auth
// en dispositivos de montacargas.
function Montacargas() {
  const { canInstall, installed, promptInstall, showIosInstructions } = useInstallPrompt();
  const [swStatus, setSwStatus] = useState("revisando...");
  const [cameraError, setCameraError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      setSwStatus("no soportado en este navegador");
      return;
    }
    navigator.serviceWorker
      .getRegistration("/montacargas/")
      .then((registration) => {
        setSwStatus(registration ? "activo" : "no registrado");
      })
      .catch(() => setSwStatus("error al consultar"));
  }, []);

  useEffect(() => {
    // Cortar la cámara si el usuario navega fuera de la vista.
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
    } catch (error) {
      setCameraError(error.message || "No se pudo acceder a la cámara");
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  return (
    <div className="container py-4" style={{ maxWidth: 480 }}>
      <h1 className="h4 mb-1">Montacargas</h1>
      <p className="text-muted small mb-4">Vista de prueba de la PWA — fuera del scope de Auth0.</p>

      <div className="card mb-3">
        <div className="card-body">
          <h2 className="h6 card-title">Estado</h2>
          <ul className="list-unstyled small mb-0">
            <li>Service worker: <strong>{swStatus}</strong></li>
            <li>Modo instalado (standalone): <strong>{installed ? "sí" : "no"}</strong></li>
          </ul>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h2 className="h6 card-title">Instalación</h2>
          {installed && <p className="small mb-0">Ya se está ejecutando como app instalada.</p>}
          {!installed && canInstall && (
            <button type="button" className="btn btn-primary btn-sm" onClick={promptInstall}>
              Instalar app
            </button>
          )}
          {!installed && !canInstall && !showIosInstructions && (
            <p className="small text-muted mb-0">
              El navegador aún no ofrece instalar (puede tardar unos segundos o requerir recargar).
            </p>
          )}
          {showIosInstructions && (
            <p className="small mb-0">
              En iPhone: toca <strong>Compartir</strong> y luego <strong>"Agregar a inicio"</strong>.
            </p>
          )}
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h2 className="h6 card-title">Prueba de cámara</h2>
          {!cameraActive && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={startCamera}>
              Probar cámara
            </button>
          )}
          {cameraActive && (
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={stopCamera}>
              Detener cámara
            </button>
          )}
          {cameraError && <p className="small text-danger mt-2 mb-0">{cameraError}</p>}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-100 mt-2 rounded"
            style={{ display: cameraActive ? "block" : "none", background: "#000" }}
          />
        </div>
      </div>
    </div>
  );
}

export default Montacargas;
